import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { PageHeader } from "@/components/Layout";

/*
 * IL CALENDARIO ECONOMICO: due schede, Myfxbook e Forex Factory (09/10, al
 * posto di TradingView).
 *
 * MYFXBOOK si incorpora col codice del SUO generatore ufficiale
 * (myfxbook.com/get-widget/13): un iframe verso `widget.mfbcdn.net`, piu' il
 * link di credito «Economic Calendar by Myfxbook.com» che il generatore mette
 * sotto. Il link resta com'e': fa parte del codice che danno.
 *
 *   - Solo alto impatto: `impacts=3` (nel generatore High=3, Medium=2, Low=1, None=0).
 *   - Le valute sono quelle che il generatore propone di serie.
 *   - L'italiano fra le lingue del widget non c'e': inglese.
 *   - IL FUSO NON SI PUO' FISSARE DA QUI. Il widget parte in GMT (non nell'ora del
 *     browser), non accetta un fuso nell'indirizzo, e lo si sceglie solo dal suo
 *     menu interno, come scarto FISSO (GMT +1, GMT +2: niente ora legale da
 *     sola). La scelta la ricorda il widget nel browser. La pagina dice quale
 *     scarto ha Roma OGGI, e che al cambio dell'ora va rimesso.
 *
 * FOREX FACTORY non si lascia incorporare: risponde `frame-ancestors 'self'`
 * (provato il 09/10 da un iframe, rifiutato dal browser). La scheda apre il
 * loro sito in una finestra nuova; i loro dati non si leggono.
 *
 * Nessuno script di terzi gira nell'origine del gestionale, dove sta il token di
 * accesso: l'iframe di Myfxbook resta nella sua origine, in sandbox (niente
 * navigazione di questa pagina, niente moduli).
 */

const MYFXBOOK_WIDGET =
  "https://widget.mfbcdn.net/widget/calendar.html?lang=en&impacts=3&symbols=AUD,CAD,CHF,CNY,EUR,GBP,JPY,NZD,USD";
const MYFXBOOK_CREDIT = "https://www.myfxbook.com/forex-economic-calendar?utm_source=widget13&utm_medium=link&utm_campaign=copyright";
const FOREX_FACTORY = "https://www.forexfactory.com/calendar";

type Scheda = "myfxbook" | "forexfactory";
/** La scheda scelta, ricordata in QUESTO browser: una comodita', e se lo storage non c'e' si parte da Myfxbook. */
const CHIAVE = "match.calendario-economico.scheda";

function schedaSalvata(): Scheda {
  try {
    const v = window.localStorage.getItem(CHIAVE);
    return v === "forexfactory" ? "forexfactory" : "myfxbook";
  } catch {
    return "myfxbook";
  }
}

function salvaScheda(s: Scheda): void {
  try {
    window.localStorage.setItem(CHIAVE, s);
  } catch {
    /* storage non disponibile (finestra privata, dati bloccati): si sceglie di nuovo la prossima volta */
  }
}

/** Lo scarto di Roma da UTC all'istante dato, in ore: 2 in ora legale, 1 in ora solare. */
function scartoRoma(at: Date = new Date()): number {
  const ora = (tz: string) => {
    const p = new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour: "2-digit", hourCycle: "h23", day: "2-digit" }).formatToParts(at);
    return Number(p.find((x) => x.type === "day")!.value) * 24 + Number(p.find((x) => x.type === "hour")!.value);
  };
  return (((ora("Europe/Rome") - ora("UTC")) % 24) + 24) % 24;
}

/**
 * Il prossimo cambio dell'ora a Roma: il giorno (gg/mm, ora italiana) e lo
 * scarto che vale da li'. Si cerca giorno per giorno a mezzogiorno UTC: il
 * cambio avviene di notte, quindi il primo mezzogiorno con lo scarto nuovo e'
 * proprio il giorno del cambio. Calcolato, non scritto: l'anno prossimo vale da
 * solo.
 */
function prossimoCambio(oggi: Date = new Date()): { giorno: string; scarto: number } | null {
  const adesso = scartoRoma(oggi);
  const base = Date.UTC(oggi.getUTCFullYear(), oggi.getUTCMonth(), oggi.getUTCDate(), 12);
  for (let d = 0; d <= 370; d++) {
    const at = new Date(base + d * 86_400_000);
    const s = scartoRoma(at);
    if (s !== adesso && at > oggi) {
      const giorno = new Intl.DateTimeFormat("it-IT", { timeZone: "Europe/Rome", day: "2-digit", month: "2-digit" }).format(at);
      return { giorno, scarto: s };
    }
  }
  return null;
}

export function EconomicCalendar() {
  const [scheda, setScheda] = useState<Scheda>(schedaSalvata);
  const scarto = scartoRoma();
  const cambio = prossimoCambio();
  const scegli = (s: Scheda) => {
    setScheda(s);
    salvaScheda(s);
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Calendario economico" subtitle="Le notizie che muovono il mercato, da Myfxbook e Forex Factory" />
      <div className="flex flex-1 flex-col gap-3 px-6 py-4">
        <div role="tablist" aria-label="Fonte del calendario" className="inline-flex self-start overflow-hidden rounded border border-line text-sm">
          {(
            [
              ["myfxbook", "Myfxbook"],
              ["forexfactory", "Forex Factory"],
            ] as const
          ).map(([v, l]) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={scheda === v}
              onClick={() => scegli(v)}
              className={`px-3 py-1.5 ${scheda === v ? "bg-raised font-medium text-ink" : "text-muted hover:text-ink"}`}
            >
              {l}
            </button>
          ))}
        </div>

        {scheda === "myfxbook" ? (
          <>
            <p className="text-xs text-warn">
              Solo notizie ad alto impatto. Il widget mostra gli orari in GMT e non si lascia impostare da qui: per l&apos;ora italiana scegli nel suo menu
              del fuso <span className="font-medium">«GMT +{scarto}:00»</span> (oggi Roma è UTC+{scarto}). Il widget ricorda la scelta in questo
              browser; è uno scarto fisso, quindi al cambio dell&apos;ora va rimesso
              {cambio ? (
                <>
                  : <span className="font-medium">dal {cambio.giorno} metti GMT +{cambio.scarto}:00</span>.
                </>
              ) : (
                "."
              )}
            </p>
            <div className="min-h-[600px] flex-1 overflow-hidden rounded-card border border-line bg-surface">
              <iframe
                title="Calendario economico Myfxbook"
                src={MYFXBOOK_WIDGET}
                className="h-full min-h-[600px] w-full"
                sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                referrerPolicy="no-referrer"
              />
            </div>
            {/* Il credito del codice ufficiale di Myfxbook, lasciato com'è. */}
            <p className="text-center text-xs text-faint">
              <a href={MYFXBOOK_CREDIT} title="Economic Calendar" target="_blank" rel="noopener" className="font-medium text-muted hover:text-ink">
                Economic Calendar
              </a>{" "}
              by Myfxbook.com
            </p>
          </>
        ) : (
          <div className="card flex flex-col items-start gap-3 p-6">
            <p className="text-sm text-ink">Forex Factory non permette di incorporare il suo calendario in altre pagine: si apre sul loro sito.</p>
            <a
              href={FOREX_FACTORY}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-sm font-medium text-on-accent hover:bg-accent/90"
            >
              Apri Forex Factory <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
            <p className="text-xs text-faint">Gli orari e i filtri sono quelli che imposti sul sito di Forex Factory. Il gestionale non legge i loro dati.</p>
          </div>
        )}
      </div>
    </div>
  );
}
